
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useBoard, useBoardContent } from "@/hooks/related-content";
import ContentDisplay, { useContentFilters } from "@/components/dashboard/ContentDisplay";
import LoadingState from "@/components/dashboard/LoadingState";
import { usePersistentViewMode } from "@/hooks/usePersistentViewMode";
import CollectionHeader from "@/components/dashboard/CollectionHeader";
import { Button } from "@/components/ui/button";
import { useContentOperations } from "@/hooks/useContentOperations";
import { useMetadata } from "@/hooks/useMetadata";
import { APP_METADATA } from "@/constants/metadata";
import { toast } from "sonner";
import { Tier1Tracking } from "@/services/analytics/Tier1Tracking";
import { useAuth } from "@/contexts/AuthContext";

const Board = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { title: defaultTitle, description: defaultDescription, image } = APP_METADATA.default;
  
  const [isLoading, setIsLoading] = useState(true);
  const { board } = useBoard(id);
  const { contentItems, isLoading: isLoadingContent, refetch } = useBoardContent(id);
  
  useMetadata({ 
    title: board ? `${board.title} - Clustr` : defaultTitle,
    description: board?.description || defaultDescription,
    image 
  });
  
  const { viewMode, setViewMode } = usePersistentViewMode("freeform");
  const { handleContentClick, handleDelete, handleArchive } = useContentOperations();
  
  // Apply filtering consistently for all view modes
  const { filteredItems } = useContentFilters(contentItems);

  // Track board navigation when component mounts and board data is available
  useEffect(() => {
    if (board && id) {
      console.log(`[BOARD] Tracking board navigation for: ${board.title} (${id})`);
      Tier1Tracking.trackNavigation('board', id, board.title, contentItems.length, user);
    }
  }, [board, id, contentItems.length, user]);

  useEffect(() => {
    console.log(`[BOARD DEBUG] Board content state updated:`, {
      boardId: id,
      boardTitle: board?.title,
      isLoadingContent,
      totalContentItems: contentItems.length,
      filteredContentItems: filteredItems.length
    });
  }, [id, board, isLoadingContent, contentItems, filteredItems]);
  
  useEffect(() => {
    if (!isLoadingContent) {
      setIsLoading(false);
    }
  }, [isLoadingContent]);

  const handleEditContent = (contentId: string, title: string) => {
    console.log(`Editing content: ${contentId}, ${title}`);
  };
  
  const handleForceRefresh = async () => {
    console.log(`[BOARD] Manual refresh triggered for board: ${id}`);
    toast.info("Refreshing board content...");
    
    try {
      await refetch();
      toast.success("Board content refreshed");
      console.log(`[BOARD] Manual refresh complete`);
    } catch (error) {
      console.error(`[BOARD] Error during manual refresh:`, error);
      toast.error("Error refreshing board content");
    }
  };

  if (isLoading) {
    return <LoadingState />;
  }

  if (!board) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2">Board not found</h1>
          <p className="text-gray-500">The board you're looking for doesn't exist or was deleted.</p>
        </div>
      </div>
    );
  }

  console.log(`[BOARD] Rendering board "${board.title}" with ${filteredItems.length} filtered items out of ${contentItems.length} total items`); 

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex-none">
        <CollectionHeader
          title={board.title}
          description={board.description || ""}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          isBoardView={true}
        />
      </div>
      
      <div className={`flex-1 ${viewMode === 'freeform' || viewMode === 'network' ? '' : 'px-7'} overflow-auto`}>
        {filteredItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-8 text-center">
            <div className="mb-4 text-gray-400">
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect width="18" height="18" x="3" y="3" rx="2" />
                <path d="M3 9h18" />
                <path d="M9 21V9" />
              </svg>
            </div>
            <h3 className="text-xl font-medium mb-2">No content on this board</h3>
            <p className="text-gray-500 max-w-md mb-6">
              Drag content items from the sidebar to add them to this board, or switch view modes to see if content appears.
            </p>
            <div className="flex gap-2">
              <Button onClick={handleForceRefresh}>Refresh Board</Button>
            </div>
          </div>
        ) : (
          <ContentDisplay 
            contentItems={filteredItems} 
            viewMode={viewMode}
            onContentClick={handleContentClick}
            onDeleteContent={handleDelete}
            onArchiveContent={handleArchive}
            onEditContent={handleEditContent}
            collectionColor={board.color}
            storeNodePositionsInTag={id}
          />
        )}
      </div>
    </div>
  );
};

export default Board;
